import { Injectable, inject } from '@angular/core';
import * as markerjs3 from '@markerjs/markerjs3';
import { MediaUtils } from '../../../utils/media.utils';

/**
 * Serviço responsável por renderizar imagens e converter para File
 */
@Injectable()
export class ImageRendererService {
    private _mediaUtils = inject(MediaUtils);

    /**
     * Renderiza uma imagem com anotações usando MarkerJS
     */
    async renderImage(
        image: HTMLImageElement,
        state: any,
        imageUrl: string
    ): Promise<File> {
        // Cria uma nova imagem para garantir que estamos usando a imagem correta
        const img = new Image();
        img.crossOrigin = 'anonymous';

        // Aguarda a imagem carregar
        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error('Erro ao carregar imagem'));
            img.src = imageUrl;
        });

        // Renderiza a imagem com todas as anotações usando o Renderer
        const renderer = new markerjs3.Renderer();
        (renderer as any).targetImage = img;

        // Define explicitamente as dimensões naturais para renderização em alta qualidade
        if (img.naturalWidth && img.naturalHeight) {
            (renderer as any).targetWidth = img.naturalWidth;
            (renderer as any).targetHeight = img.naturalHeight;
        }

        const dataUrl = await (renderer as any).rasterize(state);

        // Converte a data URL em File preservando o formato original e qualidade máxima
        return await this.dataUrlToFile(dataUrl, imageUrl);
    }

    /**
     * Converte uma data URL em um objeto File preservando qualidade máxima
     */
    private async dataUrlToFile(
        dataUrl: string,
        originalImageUrl?: string,
        filename?: string
    ): Promise<File> {
        // Detecta o formato original da imagem para preservar o tipo MIME
        let mimeType = 'image/png';
        let extension = 'png';

        if (originalImageUrl) {
            try {
                const urlLower = originalImageUrl.toLowerCase();
                if (urlLower.includes('.jpg') || urlLower.includes('.jpeg')) {
                    mimeType = 'image/jpeg';
                    extension = 'jpg';
                } else if (urlLower.includes('.png')) {
                    mimeType = 'image/png';
                    extension = 'png';
                } else if (urlLower.includes('.webp')) {
                    mimeType = 'image/webp';
                    extension = 'webp';
                } else if (urlLower.includes('.gif')) {
                    mimeType = 'image/gif';
                    extension = 'gif';
                }
            } catch (e) {
                // Se falhar, mantém PNG como padrão
            }
        }

        // Converte data URL para blob usando canvas para garantir qualidade máxima
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        return new Promise<File>((resolve, reject) => {
            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;

                if (!ctx) {
                    reject(new Error('Não foi possível criar o contexto do canvas'));
                    return;
                }

                ctx.drawImage(img, 0, 0);

                canvas.toBlob(
                    blob => {
                        if (!blob) {
                            reject(new Error('Falha ao converter imagem'));
                            return;
                        }

                        const finalMimeType = blob.type || mimeType;
                        const finalExtension =
                            finalMimeType === 'image/jpeg'
                                ? 'jpg'
                                : finalMimeType === 'image/png'
                                  ? 'png'
                                  : finalMimeType === 'image/webp'
                                    ? 'webp'
                                    : finalMimeType === 'image/gif'
                                      ? 'gif'
                                      : extension;

                        const file = new File(
                            [blob],
                            filename || `edited-image-${Date.now()}.${finalExtension}`,
                            { type: finalMimeType }
                        );
                        resolve(file);
                    },
                    mimeType,
                    mimeType === 'image/jpeg' ? 1.0 : undefined
                );
            };

            img.onerror = () => {
                fetch(dataUrl)
                    .then(response => response.blob())
                    .then(blob => {
                        const file = new File(
                            [blob],
                            filename || `edited-image-${Date.now()}.${extension}`,
                            { type: blob.type || mimeType }
                        );
                        resolve(file);
                    })
                    .catch(reject);
            };

            img.src = dataUrl;
        });
    }

    /**
     * Verifica se houve alterações nos marcadores comparando o estado inicial com o atual
     */
    hasMarkerChanges(initialState: any, currentState: any): boolean {
        if (!initialState || !currentState) {
            return false;
        }

        const initialMarkers = initialState.markers || [];
        const currentMarkers = currentState.markers || [];

        if (initialMarkers.length !== currentMarkers.length) {
            return true;
        }

        if (initialMarkers.length === 0 && currentMarkers.length === 0) {
            return false;
        }

        const normalizeMarkers = (markers: any[]) => {
            return markers
                .map(marker => {
                    const normalized = JSON.parse(JSON.stringify(marker));
                    delete normalized.id;
                    delete normalized.timestamp;
                    return normalized;
                })
                .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
        };

        const normalizedInitial = normalizeMarkers(initialMarkers);
        const normalizedCurrent = normalizeMarkers(currentMarkers);

        return JSON.stringify(normalizedInitial) !== JSON.stringify(normalizedCurrent);
    }
}

